import React, { useState, useEffect } from 'react';
import { 
  Filter, X, ChevronDown, ChevronUp, RotateCcw,
  CheckSquare, Flag, User, Calendar, Users, FolderKanban,
  Layers, GitBranch, ListTree
} from 'lucide-react';
import MultiSelectFilter from './MultiSelectFilter';
import DateRangeFilter from './DateRangeFilter';
import { InsightsBar } from './ToggleChipsFilter';
import PresetManager from './PresetManager';
import { api } from '../../api/client';

/**
 * Filter Bar 2.0 - Unified filter bar for hierarchy pages
 * Supports multi-select filters, date ranges, and quick filters
 */
const FilterBar2 = ({
  pageType = 'epics', // 'epics' | 'features' | 'stories' | 'tasks'
  filters = {},
  onFiltersChange,
  quickFilters = {},
  onQuickFiltersChange,
  counts = {}, // For InsightsBar counts
  currentUser = null,
  showInsightsBar = true,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    portfolios: [],
    programs: [],
    projects: [],
    epics: [],
    features: [],
    stories: [],
    teams: [],
    sprints: [],
    assignees: [],
    statuses: [],
    priorities: [],
  });
  const [loading, setLoading] = useState(true);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Reload cascading options when hierarchy filters change
  useEffect(() => {
    if (filters.project_id?.length > 0) {
      loadFilterOptions();
    }
  }, [filters.project_id]);

  const loadFilterOptions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.portfolio_id?.length > 0) params.portfolio_id = filters.portfolio_id[0];
      if (filters.program_id?.length > 0) params.program_id = filters.program_id[0];
      if (filters.project_id?.length > 0) params.project_id = filters.project_id[0];

      const res = await api.getHierarchyFilterOptions(params);
      setFilterOptions(res.data);
    } catch (error) {
      console.error('Failed to load filter options:', error);
      // Fallback to basic filters endpoint
      try {
        const res = await api.getFilters();
        setFilterOptions(prev => ({
          ...prev,
          portfolios: res.data.portfolios || [],
          programs: res.data.programs || [],
          projects: res.data.projects || [],
          statuses: (res.data.statuses || []).map(s => typeof s === 'string' ? { id: s, name: s } : s),
          teams: res.data.teams || [],
        }));
      } catch (e) {
        console.error('Fallback filter load failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    if (onQuickFiltersChange) {
      onQuickFiltersChange({});
    }
  };

  // Count active filters
  const activeFilterCount = Object.entries(filters)
    .filter(([_, v]) => Array.isArray(v) ? v.length > 0 : Boolean(v))
    .length;

  const hasActiveFilters = activeFilterCount > 0 || 
    Object.values(quickFilters).some(v => v === true);

  // Determine which hierarchy filters to show based on page type
  const getHierarchyFilters = () => {
    switch (pageType) {
      case 'epics':
        return ['portfolio', 'program', 'project'];
      case 'features':
        return ['portfolio', 'program', 'project', 'epic'];
      case 'stories':
        return ['portfolio', 'program', 'project', 'epic', 'feature'];
      case 'tasks':
        return ['portfolio', 'program', 'project', 'epic', 'feature', 'story'];
      default:
        return ['portfolio', 'program', 'project'];
    }
  };

  const hierarchyFilters = getHierarchyFilters();

  // Handle loading a preset
  const handleLoadPreset = (preset) => {
    onFiltersChange(preset.filters || {});
    if (onQuickFiltersChange) {
      onQuickFiltersChange(preset.quickFilters || {});
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`} data-testid="filter-bar-2">
      {/* Collapsed Header */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-[var(--clr-text-primary)] hover:text-[var(--clr-brand-primary)]"
            data-testid="filter-bar-toggle"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 bg-[var(--clr-brand-primary)] text-white text-xs rounded-full">
                {activeFilterCount}
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {/* Preset Manager */}
          <PresetManager
            pageType={pageType}
            currentFilters={filters}
            currentQuickFilters={quickFilters}
            onLoadPreset={handleLoadPreset}
          />
        </div>

        {/* Quick filter pills when collapsed */}
        {!isExpanded && hasActiveFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            {filters.status?.length > 0 && (
              <span className="px-2 py-1 bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)] text-xs rounded-full flex items-center gap-1">
                <CheckSquare className="w-3 h-3" />
                {filters.status.length} status{filters.status.length > 1 ? 'es' : ''}
                <button onClick={() => updateFilter('status', [])} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.priority?.length > 0 && (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center gap-1">
                <Flag className="w-3 h-3" />
                {filters.priority.length} priorit{filters.priority.length > 1 ? 'ies' : 'y'}
                <button onClick={() => updateFilter('priority', [])} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.assignee?.length > 0 && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                <User className="w-3 h-3" />
                {filters.assignee.length} assignee{filters.assignee.length > 1 ? 's' : ''}
                <button onClick={() => updateFilter('assignee', [])} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={clearAllFilters}
              className="text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-sev-blocked)] flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4">
          {/* Main Filters Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Status Filter - Always shown */}
            <MultiSelectFilter
              label="Status"
              icon={CheckSquare}
              value={filters.status || []}
              options={filterOptions.statuses}
              onChange={(v) => updateFilter('status', v)}
              placeholder="All Statuses"
              testId="filter-status"
            />

            {/* Priority Filter - Always shown */}
            <MultiSelectFilter
              label="Priority"
              icon={Flag}
              value={filters.priority || []}
              options={filterOptions.priorities}
              onChange={(v) => updateFilter('priority', v)}
              placeholder="All Priorities"
              testId="filter-priority"
            />

            {/* Assignee Filter */}
            <MultiSelectFilter
              label="Assignee"
              icon={User}
              value={filters.assignee || []}
              options={filterOptions.assignees?.map(a => ({ id: a, name: a })) || []}
              onChange={(v) => updateFilter('assignee', v)}
              placeholder="All Assignees"
              testId="filter-assignee"
            />

            {/* Team Filter */}
            <MultiSelectFilter
              label="Team"
              icon={Users}
              value={filters.team || []}
              options={filterOptions.teams}
              onChange={(v) => updateFilter('team', v)}
              placeholder="All Teams"
              testId="filter-team"
            />

            {/* Sprint Filter */}
            <MultiSelectFilter
              label="Sprint"
              icon={Calendar}
              value={filters.sprint || []}
              options={filterOptions.sprints}
              onChange={(v) => updateFilter('sprint', v)}
              placeholder="All Sprints"
              testId="filter-sprint"
            />

            {/* Due Date Range */}
            <DateRangeFilter
              label="Due Date"
              startDate={filters.due_date_start}
              endDate={filters.due_date_end}
              onStartDateChange={(v) => updateFilter('due_date_start', v)}
              onEndDateChange={(v) => updateFilter('due_date_end', v)}
              testId="filter-due-date"
            />
          </div>

          {/* Hierarchy Filters - Second Row */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs font-medium text-[var(--clr-text-muted)] uppercase mb-3">
              Hierarchy Filters
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {hierarchyFilters.includes('portfolio') && (
                <MultiSelectFilter
                  label="Portfolio"
                  icon={FolderKanban}
                  value={filters.portfolio_id || []}
                  options={filterOptions.portfolios}
                  onChange={(v) => updateFilter('portfolio_id', v)}
                  placeholder="All Portfolios"
                  testId="filter-portfolio"
                />
              )}

              {hierarchyFilters.includes('program') && (
                <MultiSelectFilter
                  label="Program"
                  icon={GitBranch}
                  value={filters.program_id || []}
                  options={filterOptions.programs}
                  onChange={(v) => updateFilter('program_id', v)}
                  placeholder="All Programs"
                  testId="filter-program"
                />
              )}

              {hierarchyFilters.includes('project') && (
                <MultiSelectFilter
                  label="Project"
                  icon={ListTree}
                  value={filters.project_id || []}
                  options={filterOptions.projects}
                  onChange={(v) => updateFilter('project_id', v)}
                  placeholder="All Projects"
                  testId="filter-project"
                />
              )}

              {hierarchyFilters.includes('epic') && (
                <MultiSelectFilter
                  label="Epic"
                  icon={Layers}
                  value={filters.epic_id || []}
                  options={filterOptions.epics}
                  onChange={(v) => updateFilter('epic_id', v)}
                  placeholder="All Epics"
                  testId="filter-epic"
                />
              )}

              {hierarchyFilters.includes('feature') && (
                <MultiSelectFilter
                  label="Feature"
                  icon={GitBranch}
                  value={filters.feature_id || []}
                  options={filterOptions.features}
                  onChange={(v) => updateFilter('feature_id', v)}
                  placeholder="All Features"
                  testId="filter-feature"
                />
              )}

              {hierarchyFilters.includes('story') && (
                <MultiSelectFilter
                  label="Story"
                  icon={ListTree}
                  value={filters.story_id || []}
                  options={filterOptions.stories}
                  onChange={(v) => updateFilter('story_id', v)}
                  placeholder="All Stories"
                  testId="filter-story"
                />
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-[var(--clr-text-muted)]">
              {activeFilterCount > 0 
                ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied`
                : 'No filters applied'
              }
            </span>
            <button
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 transition-colors ${
                hasActiveFilters 
                  ? 'text-[var(--clr-text-secondary)] hover:text-[var(--clr-sev-blocked)] hover:bg-red-50' 
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              data-testid="clear-all-filters"
            >
              <RotateCcw className="w-4 h-4" />
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Insights Bar (Quick Filters) */}
      {showInsightsBar && (
        <div className="border-t border-gray-100">
          <InsightsBar
            filters={quickFilters}
            onFilterChange={onQuickFiltersChange}
            counts={counts}
            currentUser={currentUser}
          />
        </div>
      )}
    </div>
  );
};

export default FilterBar2;
